import type { PreferredSlot } from '../../types/api';

/**
 * 0 = Sunday, which is what the API stores and what the Saudi school week
 * starts on. Deliberately not `Intl` day names: those follow the viewer's
 * locale *and* its first-day-of-week, and the index here is fixed.
 */
export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** `17:00:00` from the API reads better as `17:00`. */
export function trimSeconds(time: string): string {
  return time.length > 5 ? time.slice(0, 5) : time;
}

export function formatPreferredSlot(slot: PreferredSlot): string {
  return `${WEEKDAYS_SHORT[slot.weekday] ?? '?'} ${trimSeconds(slot.startLocal)}–${trimSeconds(slot.endLocal)}`;
}

/**
 * The next instant matching a parent's weekday-and-time window, as a value
 * for `<input type="datetime-local">`.
 *
 * This is a *suggestion* for the admin, computed in the browser's own zone
 * rather than the parent's — getting a real cross-zone conversion right
 * needs the IANA zone and is the kind of thing that should not be guessed
 * silently. The admin sees the parent's stated zone next to the field and
 * confirms the actual time before assigning.
 */
export function nextOccurrence(slot: PreferredSlot, from = new Date()): string {
  const [hours, minutes] = trimSeconds(slot.startLocal).split(':').map(Number);
  const target = new Date(from);
  target.setSeconds(0, 0);
  target.setHours(hours, minutes);

  const daysAhead = (slot.weekday - target.getDay() + 7) % 7;
  target.setDate(target.getDate() + daysAhead);
  if (target.getTime() <= from.getTime()) target.setDate(target.getDate() + 7);

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
}
